// TikTok Display API — talks to the Node backend under /api (same origin,
// reverse-proxied). Backend: instafarm2026-backend src/routes/tiktok.routes.ts

export interface TikTokAccount {
  openId: string;
  displayName: string;
  username: string;
}

export interface TikTokVideo {
  id: string;
  title: string;
  createTime: number;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  shareCount: number;
  shareUrl: string;
  coverImageUrl: string;
}

export interface TikTokStats {
  openId: string;
  displayName: string;
  username: string;
  followerCount: number;
  followingCount: number;
  likesCount: number;
  videoCount: number;
  videos: TikTokVideo[];
}

// Stats for specific videos (by id) of one account — for the "по ссылкам" report.
export async function getTikTokVideosByIds(
  openId: string,
  videoIds: string[],
): Promise<TikTokVideo[]> {
  const res = await fetch(`/api/tiktok/video-query/${encodeURIComponent(openId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ videoIds }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `video-query failed: ${res.status}`);
  }
  const json = (await res.json()) as { videos: TikTokVideo[] };
  return json.videos ?? [];
}

// Parse a TikTok video URL → { handle, videoId }. Handles the standard
// tiktok.com/@handle/video/<id> form (with or without query/trailing slash).
export function parseTikTokVideoUrl(url: string): { handle: string; videoId: string } | null {
  const m = /tiktok\.com\/@([\w.]+)\/video\/(\d+)/.exec(url);
  return m ? { handle: m[1], videoId: m[2] } : null;
}

export function isTikTokUrl(url: string): boolean {
  return /tiktok\.com/i.test(url);
}

// Display label for a TikTok account: prefer the @handle, fall back to the
// display name (or openId) when the handle hasn't been captured yet.
export function tiktokLabel(a: { username?: string; displayName?: string; openId?: string }): string {
  if (a.username) return `@${a.username}`;
  return a.displayName || a.openId || '';
}

// Full-page redirect into the OAuth consent flow; TikTok returns to
// /accounts-stats?tiktok=connected on success.
export const TIKTOK_CONNECT_URL = '/api/tiktok/auth';

export async function listTikTokAccounts(): Promise<TikTokAccount[]> {
  const res = await fetch('/api/tiktok/accounts');
  if (!res.ok) throw new Error(`accounts failed: ${res.status}`);
  const json = (await res.json()) as { accounts: TikTokAccount[] };
  return json.accounts ?? [];
}

// Единый кэш списка подключённых TikTok-аккаунтов: раньше семь мест дёргали
// /api/tiktok/accounts независимо (страница «Аккаунты», общая статистика,
// отчёты, статистика по ссылкам). Список меняется только при подключении
// аккаунта, поэтому кэшируем промис до явного invalidate.
// ponytail: кэш живёт до перезагрузки вкладки; TTL добавить, если список
// начнёт меняться извне (например, отзыв токена на бэке).
let accountsCache: Promise<TikTokAccount[]> | null = null;

export function getTikTokAccounts(): Promise<TikTokAccount[]> {
  if (!accountsCache) {
    accountsCache = listTikTokAccounts().catch((err) => {
      // Ошибку не кэшируем: следующий вызов пробует снова.
      accountsCache = null;
      throw err;
    });
  }
  return accountsCache;
}

/** Сбросить кэш — после подключения нового аккаунта. */
export function invalidateTikTokAccounts(): void {
  accountsCache = null;
}

export async function getTikTokStats(openId: string): Promise<TikTokStats> {
  const res = await fetch(`/api/tiktok/stats/${encodeURIComponent(openId)}`);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `stats failed: ${res.status}`);
  }
  return (await res.json()) as TikTokStats;
}

// Videos posted in [fromMs, toMs] (epoch ms), newest first, for report building.
export async function getTikTokVideosInRange(
  openId: string,
  fromMs: number,
  toMs: number,
): Promise<TikTokVideo[]> {
  const res = await fetch(
    `/api/tiktok/videos/${encodeURIComponent(openId)}?from=${fromMs}&to=${toMs}`,
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `videos failed: ${res.status}`);
  }
  const json = (await res.json()) as { videos: TikTokVideo[] };
  return json.videos ?? [];
}
